import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, Product } from "../../context/AppContext";
import { useCopy } from "../../context/CopyContext";
import { useTESEvents } from "../../hooks/useTESEvents";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";

const OfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { getCopy } = useCopy();
  const { emitBatchReturnsConfirmed, isEnabled: tesEnabled } = useTESEvents();
  const [offerProduct, setOfferProduct] = useState<any>(null);

  // Personalized recommendations - analyzes returned items to show relevant products
  useEffect(() => {
    // ============================================================================
    // PERSONALIZED RECOMMENDATIONS LOGIC (CURRENTLY ACTIVE)
    // ============================================================================
    // This section analyzes the user's returned items and recommends related products
    // ============================================================================

    // Define recommendation mapping: returned product ID -> recommended product
    const recommendationMap: Record<string, {
      name: string;
      brand: string;
      size: string;
      originalPrice: number;
      discountPercent: number;
      image: string;
    }> = {
      // Chanel Eyeshadow -> Chanel Les Beiges Palette
      "chanel-ombre-essentielle": {
        name: "Les Beiges Eyeshadow Palette",
        brand: "CHANEL",
        size: "4.5g",
        originalPrice: 59.00,
        discountPercent: 20,
        image: `/assets/images/products/chanel-les-beiges/main.png?v=${Date.now()}`
      },

      // CeraVe Cleanser -> CeraVe Cleanser Refill
      "cerave-foaming-cleanser-236ml": {
        name: "Foaming Cleanser Refill",
        brand: "CeraVe",
        size: "473mL",
        originalPrice: 18.00,
        discountPercent: 20,
        image: `/assets/images/products/cerave-foaming-cleanser-refill/main.webp?v=${Date.now()}`
      },

      // Nivea Cream -> Nivea 100mL Bottle
      "nivea-cream-75ml": {
        name: "Crème Moisturiser",
        brand: "NIVEA",
        size: "100mL",
        originalPrice: 2.50,
        discountPercent: 15,
        image: `/assets/images/products/nivea-creme-100/main.png?v=${Date.now()}`
      },

      // MAC Lipstick OR MAC Mascara -> MAC Totally Teddy Lip Kit
      "mac-lipstick": {
        name: "Totally Teddy Lip Kit",
        brand: "MAC",
        size: "Kit",
        originalPrice: 98.00,
        discountPercent: 25,
        image: `/assets/images/products/mac-totally-teddy-lip-kit/main.png?v=${Date.now()}`
      },
      "mac-mascara": {
        name: "Totally Teddy Lip Kit",
        brand: "MAC",
        size: "Kit",
        originalPrice: 98.00,
        discountPercent: 25,
        image: `/assets/images/products/mac-totally-teddy-lip-kit/main.png?v=${Date.now()}`
      },

      // Tangle Teezer Classic -> Tangle Teezer Wet Detangler
      "tangle-teezer-classic": {
        name: "Wet Detangler",
        brand: "Tangle Teezer",
        size: "Brush",
        originalPrice: 14.99,
        discountPercent: 15,
        image: `/assets/images/products/tangle-teezer-classic/main.png?v=${Date.now()}`
      },

      // Nespresso Classic (Arpeggio, Volluto) -> Classic Best Sellers Set
      "nespresso-classic": {
        name: "Classic Best Sellers Set",
        brand: "Nespresso",
        size: "50 Pods",
        originalPrice: 45.00,
        discountPercent: 15,
        image: `/assets/images/products/nespresso-classic-best-sellers/main.png?v=${Date.now()}`
      },

      // Nespresso Vertuo (Stormio, Altissio) -> Vertuo Gift Set
      "nespresso-vertuo": {
        name: "Vertuo Gift Set",
        brand: "Nespresso",
        size: "30 Pods + Accessories",
        originalPrice: 59.00,
        discountPercent: 20,
        image: `/assets/images/products/nespresso-vertuo-gift-set/main.png?v=${Date.now()}`
      }
    };

    // Get all returned items from the cart
    const returnedItems = state.empties || [];
    console.log('🛒 Returned items:', returnedItems.map(e => e.product.id));

    // Find first matching recommendation from returned items
    let selectedRecommendation = null;
    for (const empty of returnedItems) {
      const productId = empty.product.id;
      if (recommendationMap[productId]) {
        selectedRecommendation = recommendationMap[productId];
        console.log(`✅ Found recommendation for ${productId}:`, selectedRecommendation.name);
        break;
      }
    }

    // If no match found, show any recommendation from the map
    if (!selectedRecommendation) {
      const allRecommendations = Object.values(recommendationMap);
      const randomIndex = Math.floor(Math.random() * allRecommendations.length);
      selectedRecommendation = allRecommendations[randomIndex];
      console.log(`🎲 Showing random recommendation:`, selectedRecommendation.name);
    }

    // Calculate discounted price
    const discountedPrice = selectedRecommendation.originalPrice * (1 - selectedRecommendation.discountPercent / 100);

    setOfferProduct({
      name: selectedRecommendation.name,
      brand: selectedRecommendation.brand,
      size: selectedRecommendation.size,
      originalPrice: `$${selectedRecommendation.originalPrice.toFixed(2)}`,
      discountedPrice: `$${discountedPrice.toFixed(2)}`,
      discount: `${selectedRecommendation.discountPercent}% OFF!`,
      image: selectedRecommendation.image
    });

    // ============================================================================
    // HARDCODED OFFER CONFIGURATION (CURRENTLY DISABLED)
    // ============================================================================
    // To switch back to hardcoded: Comment out the personalized logic above and
    // uncomment this section
    // ============================================================================

    /*
    const hardcodedOffer = {
      name: "Foaming Cleanser Refill",
      brand: "CeraVe",
      size: "473mL",
      originalPrice: 18.00,      // Original price in dollars
      discountPercent: 20,        // Discount percentage
      image: `/assets/images/products/cerave-foaming-cleanser-refill/main.webp?v=${Date.now()}`  // Path to product image with cache-busting
    };

    console.log('🎯 Using hardcoded offer product:', hardcodedOffer.name);

    // Calculate discounted price
    const discountedPrice = hardcodedOffer.originalPrice * (1 - hardcodedOffer.discountPercent / 100);

    setOfferProduct({
      name: hardcodedOffer.name,
      brand: hardcodedOffer.brand,
      size: hardcodedOffer.size,
      originalPrice: `$${hardcodedOffer.originalPrice.toFixed(2)}`,
      discountedPrice: `$${discountedPrice.toFixed(2)}`,
      discount: `${hardcodedOffer.discountPercent}% OFF!`,
      image: hardcodedOffer.image
    });
    */
  }, [state.empties]);

  const handleNoThanks = async () => {
    console.log("User declined offer");

    // Emit TES return confirmed events for all items
    if (tesEnabled && state.empties.length > 0) {
      const products = state.empties.map(e => e.product);
      emitBatchReturnsConfirmed(products, { rewardType: 'discount' }).catch((err) => {
        console.error('[TES] Return confirmation emit failed:', err);
      });
    }

    dispatch({ type: "SET_CLAIMED_OFFER", payload: false });
    // Navigate to final confirmation/thank you screen
    navigate("/confirmation");
  };

  const handleClaimOffer = async () => {
    console.log("User claimed offer");

    // Emit TES return confirmed events for all items
    if (tesEnabled && state.empties.length > 0) {
      const products = state.empties.map(e => e.product);
      emitBatchReturnsConfirmed(products, { rewardType: 'discount' }).catch((err) => {
        console.error('[TES] Return confirmation emit failed:', err);
      });
    }

    dispatch({ type: "SET_CLAIMED_OFFER", payload: true });
    // Navigate to final confirmation/thank you screen
    navigate("/confirmation");
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="164:5036"
    >
      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-4">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-10">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={false}
              itemsNumber={state.itemsCount.toString()}
              
              
              
              
              
            />
          </div>
        </div>

        {/* Main Content Area - with custom grid */}
        <motion.div 
          className="flex-1 grid grid-rows-[auto_1fr] gap-2 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Main heading */}
          <div className="flex items-center justify-center">
            <div className="text-center w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading
                  title={getCopy('offer.personalizedTitle')}
                  subtitle={getCopy('offer.personalizedSubtitle')}
                />
              </motion.div>
            </div>
          </div>

          {/* Product Image and Details */}
          <div className="flex items-center justify-center overflow-hidden">
            {offerProduct ? (
              <motion.div
                className="w-full h-full flex flex-col items-center justify-center gap-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                {/* Product Image - Larger size */}
                <div className="flex items-center justify-center w-full flex-1 min-h-0 py-4">
                  <img
                    src={offerProduct.image}
                    alt={offerProduct.name}
                    className="w-auto h-full max-h-[650px] object-contain"
                  />
                </div>
                
                {/* Product Details - More compact */}
                <motion.div
                  className="text-center w-full flex flex-col justify-center py-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1]
                  }}
                >
                  {/* Product Name */}
                  <h2 className="font-brand-secondary font-normal text-[24px] leading-[30px] text-brand-text-primary mb-1">
                    {offerProduct.name}
                  </h2>
                  
                  {/* Product Size */}
                  <p className="font-brand-secondary font-normal text-[18px] leading-[24px] text-brand-text-muted mb-4">
                    {offerProduct.size}
                  </p>
                  
                  {/* Price and Discount */}
                  <div className="flex flex-row gap-3 items-center justify-center">
                    <span className="font-brand-secondary font-normal text-[32px] leading-[32px] text-brand-text-secondary line-through">
                      {offerProduct.originalPrice}
                    </span>
                    <span className="font-brand-secondary font-semibold text-[32px] leading-[32px] text-brand-text-primary">
                      {offerProduct.discountedPrice}
                    </span>
                    <div className="bg-brand-tertiary rounded-full px-3 py-1">
                      <span className="font-brand-secondary font-medium text-[18px] leading-[24px] text-white">
                        {offerProduct.discount}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <div className="flex items-center justify-center">
                <p className="text-brand-text-muted text-lg">Loading product offer...</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Bottom Section - No Thanks/Claim Offer buttons and footer */}
        <div className="flex flex-col gap-6 items-center mx-20 mb-6">
          {/* Buttons */}
          <div className="flex flex-row gap-10 w-full">
            <Button variant="secondary" onClick={handleNoThanks}>
              No, Thanks
            </Button>
            <Button variant="primary" onClick={handleClaimOffer}>
              Claim Offer
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferView;
